// Writing something.
//
// Who reads this used to be answered here too, in a sentence above the box. That line now
// lives above the messages instead, as a fixed heading that holds still while the thread
// scrolls — see `ThreadRoster.tsx` for the reasoning that used to live in this comment.
//
// ---------------------------------------------------------------------------
// THE PHONE
// ---------------------------------------------------------------------------
//
// Acceptance case 1 is a soft keyboard open on a phone. Three things here are load-bearing
// and all three are easy to lose:
//
//   * the textarea is 16px or larger, because iOS Safari zooms the whole page when a
//     smaller input takes focus and the layout never recovers;
//   * this element is `position: sticky` at the bottom of the scrolling column, not
//     `position: fixed`. Fixed positioning is measured against a viewport the keyboard
//     does not shrink on older iOS, which puts the box under the keys;
//   * on a phone, Enter inserts a newline and does not send. A soft keyboard has no
//     shift-Enter to fall back on, so a sending Enter would take a half-written paragraph
//     about somebody's mother away from them mid-sentence. Send is a button.
//
// On a desktop keyboard Enter sends and shift-Enter breaks the line, which is what anyone
// arriving from Slack expects. The split is decided at the keystroke by `pointer: fine` —
// a mouse or trackpad, not a finger — rather than by a stored flag, so a laptop with a
// touchscreen and an iPad with a keyboard each behave as whatever they are being used as
// at that moment. The hint under the box is hidden by the same query in `styles.css`, so
// the instruction and the behaviour cannot drift apart.
//
// ---------------------------------------------------------------------------
// WHAT A FAILED SEND SAYS
// ---------------------------------------------------------------------------
//
// It says the message was not sent, and it keeps the text in the box. It does not say the
// facilitator did not answer, because those are unrelated, and conflating them is how an
// interface ends up reporting silence as a failure.

import { useState, type FormEvent, type KeyboardEvent } from 'react';

export type ComposerProps = {
  posting: boolean;
  sendFailed: boolean;
  onSend: (body: string) => Promise<void>;
};

export function Composer({ posting, sendFailed, onSend }: ComposerProps): JSX.Element {
  const [text, setText] = useState('');

  const send = (): void => {
    const body = text.trim();
    if (body === '' || posting) {
      return;
    }
    void onSend(body).then(
      () => setText(''),
      // The text stays in the box. Losing what somebody just wrote about their own death
      // because a request failed is not recoverable by them.
      () => undefined,
    );
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    send();
  };

  const keyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }
    // Every other modifier is somebody else's shortcut; leave them alone.
    if (event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    if (!window.matchMedia('(pointer: fine)').matches) {
      return;
    }
    event.preventDefault();
    send();
  };

  return (
    <form className="composer" onSubmit={submit}>
      <div className="composer__row">
        <textarea
          className="composer__field"
          aria-label="Message"
          value={text}
          rows={2}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={keyDown}
        />
        <button
          type="submit"
          className="button button--primary composer__send"
          disabled={posting || text.trim() === ''}
        >
          Send
        </button>
      </div>
      <p className="composer__keys" aria-hidden="true">
        <kbd>↵</kbd> send · <kbd>⇧↵</kbd> newline
      </p>
      {sendFailed ? (
        <p className="composer__failed" role="status">
          That message wasn’t sent. It’s still here — try again.
        </p>
      ) : null}
    </form>
  );
}
